/**
 * @module {Channel} lib/channel
 * @parent utils.modules
 *
 * Provides a two-way, cross-domain communication channel that is built on top of
 * the `postMessage` method of the HTML 5 Messaging API.
 *
 * @option {Channel} Map
 * A two-way, cross-domain communication channel that is built on top of
 * the `postMessage` method of the HTML 5 Messaging API.
 */
define('util/channel',[
	"util/core",
	"util/class",
	"util/map",
	"promise"
], function( util, Class, Map, Promise ) {

	"use strict";

	var DEBUG = true;

	var
		global        = window,
		uid           = 0,
		boundChannels = new Map(),
		MESSAGE_TYPE  = "application/-x-message+json";

	function listen( target, event, listener ) {
		var binder;

		if ( target.addEventListener ) {
			target.addEventListener( event, listener );
			return function(){ target.removeEventListener( event, listener ) };
		}

		if ( target.attachEvent ) {
			target.attachEvent( "on"+event, handler = function() { listener.call( target, window.event ) });
			return function() { target.detachEvent( "on"+event, handler ) };
		}

		return function(){};
	}

	function getBoundChannels( window ) {
		var channels;
		// For messages sent by top-level window contexts that have at one point or
		// other switched domains, the WindowProxy object assigned to event.source
		// is no longer a subclass of Window. We use this to detect another bug where
		// Map.prototype.has(key) no longer matches these mangled window proxies
		// even though a direct strict equality comparison *does*.
		if ( window instanceof Window ) {
			channels = boundChannels.get( window );
		} else {
			boundChannels.forEach( function( value, key, map ) {
				if ( window === key ) {
					channels = value;
				}
			});
		}

		return channels;
	}

	var log = !DEBUG ? function() {} : function log() {
		var console = ( "console" in global ) ? global.console : null;

		if ( console ) {
			Function.prototype.apply.apply( console.log, [ console, arguments ]);
		}
	};

	var logAccept = !DEBUG ? function() {} : function logAccept( channel, remote, type, name ) {
		log( 
			"Channel \""+ channel + "\" accepts" +
			"\n\t" +  global.location + " <- " + remote +
			"\n\t" + String( type ).toUpperCase() + ( name ? " " + String( name ) : "" )
		);
	}

	var logSend = !DEBUG ? function() {} : function logAccept( channel, remote, type, name ) {
		log( 
			"Channel \""+ channel + "\" sends" +
			"\n\t" + global.location + " -> " + remote +
			"\n\t" + String( type ).toUpperCase() + ( name ? " " + String( name ) : "" )
		);
	}

	function tryParseJSON( json ) {
		try {
			return JSON.parse( json );
		} catch( err ) {
			return undefined;
		}
	}

	listen( global, "message", function( event ) {
		var
			channels,
			channel,
			data,
			i,
			n;

		// Filters against the message source being the remote endpoint
		// of a registered channel.
		channels = getBoundChannels( event.source );
		if ( channels === undefined ) return;

		// Try to parse the message payload as JSON, then filter against invalid
		// JSON and against the type marker for a channel message not being present.
		data = tryParseJSON( event.data );
		if (( data === undefined ) || ( data.type !== MESSAGE_TYPE )) return;

		// Accept via the first channel that may do so.
		for ( i = 0, n = channels.length ; i !== n ; ++i ) {
			channel = channels[ i ];
			if ( channel.accept( event.origin, data.name, data.msg )) {
				break;
			}
		}
	});


	var Channel = Class.create({
		constructor : function Channel( options ) {
			var
				me   = this,
				impl = new ChannelImpl( options );

			if ( !( me instanceof Channel )) {
				return new Channel( options );
			}

			var channels = getBoundChannels( options.remote );
			if ( channels === undefined ) {
				channels = [];
				boundChannels.set( options.remote, channels );
			}

			channels.push( impl );

			me.post = function( name, value ) {
				return impl.post( name, value );
			};

			me.dispatch = function( name, value ) {
				impl.dispatch( name, value );
			};

			me.listen = function( name, listener ) {
				impl.listen( name, listener );
				return me;
			};

			me.open  = function() { impl.open(); return me; }
			me.close = function() { impl.close(); return me; }
		}
	});

	var ChannelImpl = Class.create( Channel, {
		constructor : function ChannelImpl( options ) {
			var me = this,
				o = me.options = {};

			if ( !options.remote || !( "postMessage" in options.remote )) {
				throw new Error( "Invalid remote endpoint" );
			}

			if ( !options.name || ( "string" !== typeof options.name )) {
				throw new Error( "Invalid channel name" );
			}

			o.remote       = options.remote;
			o.name         = options.name;
			o.remoteOrigin = String( options.remoteOrigin || "*" );
			o.allowOrigins = null;
			if ( util.isArray( options.allowOrigins )) {
				var n = options.allowOrigins.length;
				if ( n > 0 ) {
					o.allowOrigins = {};
					while( --n !== -1 ) { o.allowOrigins[ "%%" + String( options.allowOrigins[ n ])] = true; }
				}
			}

			me.buffer    = [];
			me.listeners = {};
			me.tickets   = new Map();
			me.state     = "closed";
		},

		accept : function( origin, name, msg ) {
			var me = this,
				o = me.options,
				iterator,
				listener,
				result,
				ticket;

			// Filters against channel name.
			if ( name !== o.name ) return false;

			// Filters against white-listed origins, if any white-list is set up.
			if (( o.allowOrigins != null ) && !(( "%%" + origin ) in o.allowOrigins )) {
				return false;
			}

			logAccept( name, msg.href || origin, msg.type, msg.name );

			switch( String( msg.type )) {
				case "open":
					if ( me.state === "half-open" ) {
						log ( "Opened channel \"" + me.options.name + "\" at " + global.location );

						me.state = "open";
						me.flush();
					}
					if ( msg.name === "ack" ) {
						log ( "Acknowledging opened channel \"" + me.options.name + "\" at " + global.location )
						me.send( "open" );
					}
					break;
				case "close":
					log ( "Closed channel \"" + me.options.name + "\" at " + global.location )

					me.state = "closed";
					me.reset();
					break;
				case "post":
					listener = me.listeners[ "%%" + msg.name ];
					if ( listener ) {
						result = listener.call( global, msg.value );
						if ( msg.ticket ) {
							Promise
								.resolve( result )
								.then( function( value ) {
									me.send( "reply", "ok", value, msg.ticket );
								})
								.catch( function( value ) {
									value = ( value instanceof Error ) ? value.message : String( value );
									me.send( "reply", "error", value, msg.ticket );
								});
						}
					}
					break;
				case "reply":
					ticket = me.tickets.get( msg.ticket );
					if ( ticket !== undefined ) {
						me.tickets["delete"]( ticket.id );
						if ( msg.name !== "error" ) {
							ticket.resolve( msg.value );
						} else {
							ticket.reject( new Error( msg.value ));
						}
					}
					break;
				default:
					return false;
			}

			return true;
		},
		
		flush : function() {
			var me = this, o = me.options,
				buffer = me.buffer,
				i, n;

			me.buffer = [];

			for ( i = 0, n = buffer.length ; i !== n ; ++i ) {
				buffer[ i ]();
			}
		},

		send : function( type, name, value, ticket ) {
			var me = this, o = me.options,
				msg = { type : type },
				send,
				json;

			if ( name   != null ) { msg.name   = name   }
			if ( value  != null ) { msg.value  = value  }
			if ( ticket != null ) { msg.ticket = ticket }

			if ( DEBUG ) { msg.href = global.location.href }

			json = JSON.stringify({ type : MESSAGE_TYPE, msg : msg, name : o.name });

			if ( ticket != null ) {
				ticket = { id : ticket };
				ticket.promise = new Promise( function( resolve, reject ) {
					ticket.resolve = function( value ) { resolve( value ); };
					ticket.reject  = function( error ) { reject( error ); };
				});
				me.tickets.set( ticket.id, ticket );
			}

			send = function() {
				logSend( o.name, o.remoteOrigin, msg.type, msg.name );
				o.remote.postMessage( json, o.remoteOrigin );
			}

			if (( me.state === "half-open" ) && (( type === "post" ) || ( type === "reply" ))) {
				me.buffer.push( send );
			} else {
				send();
			}

			if ( ticket != null ) {
				return ticket.promise;
			}
		},

		reset : function() {
			var me = this,
				iterator = me.tickets.values(),
				ticket;

			while( true ) {
				ticket = iterator.next();
				if ( ticket.done ) break;

				ticket.value.reject( "reset" );
			}

			me.buffer.length = 0;
			me.tickets.clear();
		},

		post : function( name, value ) {
			return this.send( "post", name, value, ++uid );
		},

		dispatch : function( name, value ) {
			return this.send( "post", name, value );
		},

		listen : function( name, listener ) {
			var me = this;

			if ( "function" !== typeof listener ) {
				throw new TypeError( "Not a valid function" );
			}

			if (( "%%" + name ) in me.listeners ) {
				throw new Error( "Listener for message type '" + name + "' already exists." )
			}

			me.listeners[ "%%" + name ] = listener;
		},

		open : function() {
			var me = this;

			if ( me.state !== "closed" ) return;

			log ( "Opening channel \"" + me.options.name + "\" at " + global.location )

			me.state = "half-open";
			me.send( "open", "ack" );
		},

		close : function() {
			var me = this;

			me.state = "closed";
			me.reset();
			me.send( "close" );
		}
	});


	Channel.build = function( options ) {
		return new Channel( options );
	}

	return Channel;

});
